import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Sparkles, Crown, FileText, CalendarDays, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import PageHeader from "@/components/app/PageHeader";
import { startCheckout, formatPrice, isPremium } from "@/lib/premium";

const PLANS = [
  { key: "pdf_single", id: "single", icon: FileText, name: "planSingleName", desc: "planSingleDesc", period: "oneTime", cta: "ctaSingle", feats: ["featOne", "featDeluxe", "featReadings"], tint: "bg-ramune/40" },
  { key: "premium_monthly", id: "monthly", icon: CalendarDays, name: "planMonthlyName", desc: "planMonthlyDesc", period: "perMonth", cta: "ctaMonthly", feats: ["featDeluxe", "featReadings", "featKanaMaster", "featKanjiMaster", "featFuture"], tint: "bg-lavender/50" },
  { key: "premium_lifetime", id: "lifetime", icon: Crown, name: "planLifetimeName", desc: "planLifetimeDesc", period: "payOnce", cta: "ctaLifetime", feats: ["featDeluxe", "featReadings", "featKanaMaster", "featKanjiMaster", "featFuture"], tint: "gold-badge", popular: true },
];

export default function PricingPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prices, setPrices] = useState({});
  const [busy, setBusy] = useState(null);
  const premium = isPremium(user);

  useEffect(() => {
    api.get("/payments/plans").then(({ data }) => setPrices(Object.fromEntries(data.map((p) => [p.lookup_key, p]))));
  }, []);

  const choose = async (plan) => {
    if (plan.key === "pdf_single") { navigate("/pdf#premium"); return; }
    if (!user || user === false) { toast(t("loginToBuy")); navigate("/login"); return; }
    setBusy(plan.key);
    try { await startCheckout(plan.key); }
    catch { toast.error("Checkout failed"); setBusy(null); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <PageHeader eyebrow={t("pricingEyebrow")} title={t("pricingTitle")} subtitle={t("pricingSub")}
        right={user && user !== false && (
          <div className="kawaii-chip !text-sm !px-4 !py-2" data-testid="current-plan-chip">
            <Sparkles className="w-4 h-4 text-sakura-deep" strokeWidth={2.5} /> {t("currentPlan")}: {t(user.premium?.plan === "lifetime" ? "planLifetime" : user.premium?.plan === "monthly" ? "planMonthly" : "planFree")}
          </div>
        )} />

      <div className="grid md:grid-cols-3 gap-6 items-stretch">
        {PLANS.map((p, i) => {
          const price = prices[p.key];
          const Icon = p.icon;
          const owned = premium && p.key !== "pdf_single";
          return (
            <div key={p.key} className={`kawaii-card p-7 flex flex-col gap-5 relative rise-in rise-in-${i + 1} ${p.popular ? "shimmer-border md:-translate-y-3" : ""}`} data-testid={`pricing-card-${p.id}`}>
              {p.popular && <span className="absolute -top-3 left-6 kawaii-chip gold-badge shadow-glow"><Crown className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("bestValue")}</span>}
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${p.tint}`}><Icon className="w-5 h-5" strokeWidth={2.5} /></div>
              <div>
                <div className="font-display text-2xl text-ink">{t(p.name)}</div>
                <p className="text-sm text-ink-soft mt-1 leading-relaxed">{t(p.desc)}</p>
              </div>
              <div className="flex items-end gap-2" data-testid={`pricing-price-${p.id}`}>
                <span className="font-display text-4xl sm:text-5xl text-ink">{price ? formatPrice(price.amount, price.currency) : "—"}</span>
                <span className="text-sm text-ink-soft font-bold pb-2">{t(p.period)}</span>
              </div>
              <ul className="space-y-2.5 flex-1">
                {p.feats.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-ink"><span className="mt-0.5 inline-flex w-5 h-5 rounded-full bg-matcha items-center justify-center shrink-0"><Check className="w-3 h-3" strokeWidth={3} /></span>{t(f)}</li>
                ))}
              </ul>
              <button data-testid={`pricing-${p.id}-btn`} onClick={() => choose(p)} disabled={busy === p.key || owned}
                className={`kawaii-btn w-full ${p.popular ? "kawaii-btn-primary" : "kawaii-btn-ink"}`}>
                {owned ? t("premiumUnlocked") : busy === p.key ? "…" : t(p.cta)}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-3 text-sm text-ink-soft">
        <ShieldCheck className="w-4 h-4" strokeWidth={2.5} /> {t("securePay")}
        <span>·</span>
        <Link to="/pdf" className="underline font-bold">{t("goToSheets")}</Link>
      </div>
    </div>
  );
}
